import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useWebgate } from '@xvia/webgate-connect';
import { PageLoadingSkeleton } from './PageLoadingSkeleton';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Home fica pública (webgate.yaml: public: true); solicitar, acompanhar e
 * editar perfil exigem login gov.br.
 *
 * `initialized` só vira true quando o portal responde (com token ou com
 * "não autenticado") — fora do portal (dev local sem o shell) essa resposta
 * nunca chega, então o próprio `getAccessToken()` da lib já teria estourado
 * o timeout dela (3s) sem marcar `initialized`. Por isso o `timedOut` local:
 * sem ele essa rota ficaria carregando pra sempre em vez de negar acesso.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { accessToken, initialized, portalConnect } = useWebgate();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (accessToken || initialized) return undefined;

    const timer = setTimeout(() => setTimedOut(true), 3500);
    return () => clearTimeout(timer);
  }, [accessToken, initialized]);

  if (accessToken) return <>{children}</>;

  if (!initialized && !timedOut) {
    return <PageLoadingSkeleton />;
  }

  if (portalConnect?.isRunningInPortal()) {
    portalConnect.login();
    return <PageLoadingSkeleton />;
  }

  return <Navigate to="/" replace state={{ authRequired: true }} />;
}
